/**
 * NeighborhoodHero — Weidner Via6 page hero for /neighborhood.
 * See `FloorPlansHero.tsx` and `_PageImageHeroBody.tsx` for the shared
 * pattern. Owns its slot registration, contentSchema, and hidden
 * literal-path anchors; visual rendering is delegated to the body.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Neighborhood hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function NeighborhoodHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="neighborhoodhero.title"
        bgPath="neighborhoodhero.bg_image"
        bgDef={bgDef}
        defaultTitle="Neighborhood"
        keywords={['neighborhood', 'exterior', 'street', 'cityscape', 'skyline', 'view']}
        pageHint="neighborhoodhero"
        altText="Neighborhood hero image"
      />

      <RichTextContent
        path="neighborhoodhero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="neighborhoodhero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodHero',
  displayName: 'Neighborhood Hero',
  tokens: {
    'neighborhoodhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Neighborhood',
    },
  },
  slotGroups: {
    'neighborhoodhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Neighborhood hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(NeighborhoodHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodHero;
